import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { tok, LABEL, MONO, panelSx, titleSx } from './ui';

// Monthly returns (API monthlyReturns), years × months, on a diverging scale: down → neutral → up.
// One title. No client-computed year totals: the API does not supply them.
const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const tone = (v, maxAbs) => {
  if (v === null || v === undefined) return { bg: 'transparent', fg: tok('ink-30') };
  if (Math.abs(v) < 0.05) return { bg: tok('ink-06'), fg: tok('ink-55') };
  const step = Math.min(4, Math.ceil((Math.abs(v) / Math.max(maxAbs, 0.1)) * 4)); // 1..4
  const alpha = [0, 15, 30, 50, 75][step];
  return { bg: `var(--dwlf-${v > 0 ? 'up' : 'down'}-${alpha})`, fg: tok(step >= 3 ? 'bg' : 'text') };
};

export default function MonthlyHeatmap({ monthlyReturns }) {
  const { years, grid, maxAbs } = useMemo(() => {
    const g = new Map();
    let m = 0;
    (monthlyReturns || []).forEach((r) => {
      const [y, mo] = String(r.month || '').split('-').map(Number);
      if (!y || !mo || r.returnPercent === null || r.returnPercent === undefined) return;
      if (!g.has(y)) g.set(y, Array(12).fill(null));
      g.get(y)[mo - 1] = r.returnPercent;
      m = Math.max(m, Math.abs(r.returnPercent));
    });
    return { years: [...g.keys()].sort(), grid: g, maxAbs: m };
  }, [monthlyReturns]);

  return (
    <Box sx={panelSx}>
      <Typography component="h2" sx={titleSx}>Monthly returns</Typography>
      {years.length === 0 && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-55') }}>No monthly returns in this result.</Typography>}
      {years.length > 0 && (
        <Box role="table" aria-label="Monthly returns by year" sx={{ display: 'grid', gridTemplateColumns: '44px repeat(12, minmax(0, 1fr))', gap: '3px', fontFamily: MONO }}>
          <Box role="row" sx={{ display: 'contents' }}>
            <Box component="span" />
            {MONTHS.map((mo, i) => <Box key={i} component="span" role="columnheader" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50'), textAlign: 'center' }}>{mo}</Box>)}
          </Box>
          {years.map((y) => (
            <Box role="row" key={y} sx={{ display: 'contents' }}>
              <Box component="span" role="rowheader" sx={{ fontSize: '0.6875rem', color: tok('ink-55'), alignSelf: 'center' }}>{y}</Box>
              {grid.get(y).map((v, i) => {
                const c = tone(v, maxAbs);
                return (
                  <Box key={i} component="span" role="cell" title={v === null ? 'No data' : `${y}-${String(i + 1).padStart(2, '0')}: ${v.toFixed(2)}%`}
                    sx={{ height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', backgroundColor: c.bg, color: c.fg, border: v === null ? `1px dashed ${tok('ink-08')}` : 'none' }}>
                    {v === null || Math.abs(v) < 0.05 ? '' : v.toFixed(1)}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
